// Lecteur de document : ouverture directe à la page demandée, rendu PDF (pdf.js) dans un canvas, texte de la page à côté
// avec l'extrait demandé surligné et sa localisation. Tableurs / texte : texte extrait par feuille ou par page. Image : l'image.
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { useParams, useSearchParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight, ExternalLink, Maximize2, ZoomIn, ZoomOut } from 'lucide-react';
import type { PDFDocumentProxy, RenderTask } from 'pdfjs-dist';
import workerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';
import { api } from '../../lib/api.ts';
import { date, dateHeure } from '../../lib/format.ts';
import { Alerte, Badge, Bouton, Chargement, EntetePage, EtatErreur, FilContexte, LienBouton, MarqueIa, Panneau, Valeur } from '../../components/ui.tsx';
import { RelanceLecture } from './commun.tsx';
import { BadgeStatutDocument } from '../../components/statuts.tsx';
import { filDocument } from './PageDocument.tsx';
import { LANGUES, libelleType, tailleFichier, type Cadre, type Document, type Version } from './commun.tsx';

export interface EtatLectureOptique { pagesLues: number; pagesALire: number; peutRelancer: boolean; possible: boolean; raison: string | null }
interface Lecture extends Cadre {
  version: Version; document: Document; pages: { page_no: number; has_text: boolean; ocr?: boolean }[]; lecture_optique: EtatLectureOptique;
  versions: { id: string; version_label: string; status: string; created_at: string }[];
}
interface PageTexte { page_no: number; text: string; has_text: boolean; ocr?: boolean; extraits: { id: string; para_no: number; locator: string | null; text: string; refs: string[]; ocr?: boolean }[] }

const EN_COURS = ['quarantaine', 'en_traitement'];

export function PageLecteur() {
  const { versionId } = useParams();
  const [params, setParams] = useSearchParams();
  const extraitId = params.get('extrait');
  const pageDemandee = Math.max(1, Number.parseInt(params.get('page') ?? '1', 10) || 1);

  const q = useQuery({
    queryKey: ['lecture', versionId], queryFn: () => api.get<Lecture>(`/api/documents/versions/${versionId}`),
    refetchInterval: (r) => (r.state.data && EN_COURS.includes(r.state.data.version.status) ? 2000 : false),
  });
  const [nbPagesPdf, setNbPagesPdf] = useState<number | null>(null);

  const v = q.data?.version;
  const estPdf = v?.mime === 'application/pdf';
  const estImage = !!v?.mime.startsWith('image/');
  const nbPages = (estPdf ? nbPagesPdf : null) ?? v?.page_count ?? (q.data?.pages.length || null);
  const page = nbPages ? Math.min(pageDemandee, nbPages) : pageDemandee;

  const allerA = useCallback((n: number) => {
    const p = new URLSearchParams(params);
    p.set('page', String(n));
    setParams(p, { replace: true });
  }, [params, setParams]);

  const texte = useQuery({
    queryKey: ['lecture-page', versionId, page], enabled: !!q.data && q.data.pages.some((p) => p.page_no === page),
    queryFn: () => api.get<PageTexte>(`/api/documents/versions/${versionId}/pages/${page}`),
  });

  if (q.isLoading) return <Chargement lignes={6} libelle="Ouverture du document…" />;
  if (q.isError || !q.data || !v) return <EtatErreur erreur={q.error} reessayer={() => q.refetch()} />;
  const doc = q.data.document;
  const fichier = `/api/documents/versions/${v.id}/fichier`;
  const unite = v.mime.includes('spreadsheetml') ? 'Feuille' : 'Page';
  const precedente = page > 1 ? () => allerA(page - 1) : undefined;
  const suivante = nbPages && page < nbPages ? () => allerA(page + 1) : undefined;
  const plusRecente = q.data.versions[0];
  const imageLue = q.data.pages.some((p) => p.has_text);

  return (
    <div className="pile-l doc-module">
      <EntetePage fil={<FilContexte noeuds={filDocument(doc, q.data, v.version_label)} />}
        titre={doc.title}
        sousTitre={<span className="rangee" style={{ gap: 8 }}>
          <span>{[doc.manufacturer ?? 'Fabricant inconnu', doc.range_name, libelleType(doc.doc_type), `version ${v.version_label}`].filter(Boolean).join(' · ')}</span>
          <BadgeStatutDocument statut={v.status} />
        </span>}
        actions={<a className="btn btn-compact" href={fichier} target="_blank" rel="noopener"><ExternalLink aria-hidden />Fichier d’origine</a>} />

      {['a_valider', 'obsolete', 'rejete'].includes(v.status) && (
        <Alerte ton="ambre" titre={v.status === 'a_valider' ? 'Document non validé' : v.status === 'obsolete' ? 'Version obsolète' : 'Version rejetée'}
          action={plusRecente && plusRecente.id !== v.id ? <LienBouton compact to={`/documentation/lecteur/${plusRecente.id}`}>Version {plusRecente.version_label}</LienBouton> : undefined}>
          Ne pas citer comme référence sans vérification.{v.status_detail && v.status !== 'a_valider' ? ` ${v.status_detail}` : ''}
        </Alerte>
      )}
      {EN_COURS.includes(v.status) && (
        <Alerte ton="info" titre="Lecture du document en cours">
          {v.status_detail?.startsWith('Lecture optique') ? `${v.status_detail} ` : ''}Le texte et les extraits apparaîtront à la fin du traitement.
        </Alerte>
      )}
      {!EN_COURS.includes(v.status) && <RelanceLecture versionId={v.id} lecture={q.data.lecture_optique} relue={() => q.refetch()} />}

      <div className={`doc-lecteur ${estPdf || estImage ? '' : 'doc-lecteur-texte-seul'}`}>
        {(estPdf || estImage) && (
          <section className="doc-visionneuse" aria-label="Document">
            {estPdf
              ? <VisionneusePdf url={fichier} page={page} surNbPages={setNbPagesPdf} precedente={precedente} suivante={suivante} titre={doc.title}
                  barre={<BarrePages page={page} nbPages={nbPages} unite={unite} precedente={precedente} suivante={suivante} allerA={allerA} />} />
              : <div className="doc-image"><img src={fichier} alt={`${doc.title}, version ${v.version_label}`} /></div>}
          </section>
        )}

        <section className="doc-texte" aria-label={`Texte de la ${unite.toLowerCase()} ${page}`}>
          {!estPdf && !estImage && <BarrePages page={page} nbPages={nbPages} unite={unite} precedente={precedente} suivante={suivante} allerA={allerA} />}
          {estImage && !imageLue && !EN_COURS.includes(v.status) && (
            <Alerte ton="info" titre="Texte de l’image non lu">{q.data.lecture_optique.raison ?? 'Consultez l’image et saisissez les informations utiles.'}</Alerte>
          )}
          {(!estImage || imageLue || EN_COURS.includes(v.status)) && (
            <TextePage texte={texte} page={page} unite={unite} extraitId={extraitId} pages={q.data.pages} enCours={EN_COURS.includes(v.status)}
              raisonSansLecture={q.data.lecture_optique.raison} />
          )}
        </section>
      </div>

      <details className="panneau doc-infos">
        <summary>Informations sur le document et la version</summary>
        <dl className="fiche">
          <div><dt>Fabricant</dt><dd><Valeur v={doc.manufacturer} /></dd></div>
          <div><dt>Gamme</dt><dd><Valeur v={doc.range_name} /></dd></div>
          <div><dt>Références produit</dt><dd>{doc.product_refs.length ? <span className="doc-refs">{doc.product_refs.map((r) => <span key={r} className="mono doc-ref">{r}</span>)}</span> : <span className="inconnu">Aucune</span>}</dd></div>
          <div><dt>Versions couvertes</dt><dd><Valeur v={doc.versions_covered} inconnu="Non précisées" /></dd></div>
          <div><dt>Langue</dt><dd><Valeur v={doc.language ? LANGUES[doc.language] ?? doc.language : null} /></dd></div>
          <div><dt>Provenance</dt><dd><Valeur v={doc.provenance} inconnu="Non renseignée" /></dd></div>
          <div><dt>Droits d’utilisation</dt><dd><Valeur v={doc.rights} inconnu="Non renseignés" /></dd></div>
          <div><dt>Date de publication</dt><dd><Valeur v={doc.published_on ? date(doc.published_on) : null} /></dd></div>
          <div><dt>Date de collecte</dt><dd>{date(v.collected_at)}</dd></div>
          <div><dt>Dernière vérification</dt><dd>{v.last_checked_at ? dateHeure(v.last_checked_at) : <span className="inconnu">Jamais vérifiée</span>}</dd></div>
          <div><dt>Revue</dt><dd>{v.reviewed_at ? `${dateHeure(v.reviewed_at)}${v.relecteur ? ` par ${v.relecteur}` : ''}` : <span className="inconnu">Pas encore relue</span>}</dd></div>
          <div><dt>Fichier</dt><dd className="petit">{v.file_name} · {tailleFichier(v.size_bytes)}{nbPages ? ` · ${nbPages} ${unite.toLowerCase()}${nbPages > 1 ? 's' : ''}` : ''}</dd></div>
          {v.empreinte && <div><dt>Empreinte SHA-256</dt><dd className="mono tres-petit">{v.empreinte}</dd></div>}
        </dl>
        {q.data.versions.length > 1 && (
          <div className="doc-autres-versions">
            <span className="petit secondaire">Autres versions :</span>
            {q.data.versions.filter((x) => x.id !== v.id).map((x) => (
              <LienBouton key={x.id} compact variante="fantome" to={`/documentation/lecteur/${x.id}`}>Version {x.version_label}</LienBouton>
            ))}
          </div>
        )}
      </details>
    </div>
  );
}

// ─── Navigation entre pages (boutons ≥ 44 px, saisie du numéro) ───
function BarrePages({ page, nbPages, unite, precedente, suivante, allerA }:
  { page: number; nbPages: number | null; unite: string; precedente?: () => void; suivante?: () => void; allerA: (n: number) => void }) {
  const [saisie, setSaisie] = useState(String(page));
  useEffect(() => { setSaisie(String(page)); }, [page]);
  const valider = () => {
    const n = Number.parseInt(saisie, 10);
    if (Number.isInteger(n) && n >= 1 && (!nbPages || n <= nbPages)) allerA(n); else setSaisie(String(page));
  };
  return (
    <div className="doc-barre-pages" role="group" aria-label="Navigation dans le document">
      <Bouton className="btn-icone" variante="secondaire" disabled={!precedente} onClick={precedente} aria-label={`${unite} précédente`}><ChevronLeft aria-hidden /></Bouton>
      <label className="doc-numero">
        <span className="visuellement-cache">Numéro de {unite.toLowerCase()}</span>
        <span aria-hidden className="petit secondaire">{unite}</span>
        <input className="saisie chiffre" inputMode="numeric" value={saisie} onChange={(e) => setSaisie(e.target.value.replace(/\D/g, '').slice(0, 5))}
          onBlur={valider} onKeyDown={(e) => { if (e.key === 'Enter') valider(); }} />
        <span className="petit secondaire">{nbPages ? `sur ${nbPages}` : ''}</span>
      </label>
      <Bouton className="btn-icone" variante="secondaire" disabled={!suivante} onClick={suivante} aria-label={`${unite} suivante`}><ChevronRight aria-hidden /></Bouton>
    </div>
  );
}

// ─── Rendu PDF ───
let pdfjsCharge: Promise<typeof import('pdfjs-dist')> | null = null;
function chargerPdfjs() {
  pdfjsCharge ??= import('pdfjs-dist').then((lib) => { lib.GlobalWorkerOptions.workerSrc = workerUrl; return lib; });
  return pdfjsCharge;
}

function VisionneusePdf({ url, page, surNbPages, precedente, suivante, titre, barre }:
  { url: string; page: number; surNbPages: (n: number) => void; precedente?: () => void; suivante?: () => void; titre: string; barre: ReactNode }) {
  const cadre = useRef<HTMLDivElement>(null);
  const canvas = useRef<HTMLCanvasElement>(null);
  const [pdf, setPdf] = useState<PDFDocumentProxy | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [zoom, setZoom] = useState<'largeur' | number>('largeur');
  const [echelle, setEchelle] = useState<number | null>(null);
  const [largeur, setLargeur] = useState(0);
  const [rendu, setRendu] = useState(false);

  useEffect(() => {
    let annule = false;
    let tache: { destroy(): Promise<void> } | null = null;
    setPdf(null); setErreur(null);
    chargerPdfjs().then((lib) => {
      if (annule) return;
      const t = lib.getDocument({ url, disableRange: true, withCredentials: true });
      tache = t;
      return t.promise.then((p) => { if (!annule) { setPdf(p); surNbPages(p.numPages); } });
    }).catch((e) => { if (!annule) setErreur(e?.name === 'PasswordException' ? 'PDF protégé par mot de passe : affichage impossible.' : 'Impossible d’afficher ce PDF. Ouvrez le fichier d’origine.'); });
    return () => { annule = true; tache?.destroy().catch(() => {}); };
  }, [url]);

  // Largeur disponible (ajustement à la largeur, y compris après rotation de l'écran)
  useLayoutEffect(() => {
    const el = cadre.current;
    if (!el) return;
    const mesurer = () => setLargeur(el.clientWidth);
    mesurer();
    const obs = new ResizeObserver(mesurer);
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    if (!pdf || !canvas.current || !largeur) return;
    let tache: RenderTask | null = null;
    let annule = false;
    setRendu(false);
    pdf.getPage(Math.min(page, pdf.numPages)).then((p) => {
      if (annule || !canvas.current) return;
      const base = p.getViewport({ scale: 1 });
      const e = zoom === 'largeur' ? Math.max(0.2, (largeur - 2) / base.width) : zoom;
      setEchelle(e);
      const vue = p.getViewport({ scale: e });
      const dpr = Math.min(window.devicePixelRatio || 1, 3);
      const c = canvas.current;
      c.width = Math.floor(vue.width * dpr); c.height = Math.floor(vue.height * dpr);
      c.style.width = `${Math.floor(vue.width)}px`; c.style.height = `${Math.floor(vue.height)}px`;
      tache = p.render({ canvas: c, viewport: vue, transform: dpr !== 1 ? [dpr, 0, 0, dpr, 0, 0] : undefined });
      return tache.promise.then(() => { if (!annule) setRendu(true); });
    }).catch((e) => { if (e?.name !== 'RenderingCancelledException' && !annule) setErreur('Cette page n’a pas pu être affichée.'); });
    return () => { annule = true; tache?.cancel(); };
  }, [pdf, page, zoom, largeur]);

  const touche = (e: KeyboardEvent) => {
    if (e.key === 'ArrowLeft' && precedente) { e.preventDefault(); precedente(); }
    if (e.key === 'ArrowRight' && suivante) { e.preventDefault(); suivante(); }
  };
  const pas = (f: number) => setZoom(Math.min(4, Math.max(0.25, Math.round((echelle ?? 1) * f * 100) / 100)));

  return (
    <div className="pile" style={{ gap: 8 }}>
      <div className="doc-outils">
      {barre}
      <div className="doc-zoom" role="group" aria-label="Zoom">
        <Bouton compact variante={zoom === 'largeur' ? 'secondaire' : 'fantome'} aria-pressed={zoom === 'largeur'} icone={<Maximize2 aria-hidden />}
          onClick={() => setZoom('largeur')} aria-label="Ajuster à la largeur" title="Ajuster à la largeur"><span className="doc-zoom-libelle">Ajuster à la largeur</span></Bouton>
        <Bouton className="btn-icone" variante="fantome" aria-label="Zoom arrière" onClick={() => pas(1 / 1.25)} disabled={!pdf}><ZoomOut aria-hidden /></Bouton>
        <span className="petit secondaire chiffre doc-zoom-valeur" aria-live="polite">{echelle ? `${Math.round(echelle * 100)} %` : ''}</span>
        <Bouton className="btn-icone" variante="fantome" aria-label="Zoom avant" onClick={() => pas(1.25)} disabled={!pdf}><ZoomIn aria-hidden /></Bouton>
      </div>
      </div>
      <div ref={cadre} className="doc-canvas-cadre">
        {erreur && <Alerte ton="danger" titre="Affichage impossible">{erreur}</Alerte>}
        {!erreur && !pdf && <Chargement lignes={4} libelle="Chargement du PDF…" />}
        <canvas ref={canvas} tabIndex={0} className="doc-canvas" hidden={!!erreur || !pdf} aria-busy={!rendu}
          aria-label={`${titre}, page ${page}${pdf ? ` sur ${pdf.numPages}` : ''}. Flèches gauche et droite pour changer de page ; le texte de la page est à côté.`}
          onKeyDown={touche} />
      </div>
    </div>
  );
}

// ─── Texte de la page et extraits localisés ───
function TextePage({ texte, page, unite, extraitId, pages, enCours, raisonSansLecture }: {
  texte: { data?: PageTexte; isLoading: boolean; isError: boolean; error: unknown; refetch: () => void };
  page: number; unite: string; extraitId: string | null; pages: { page_no: number; has_text: boolean }[]; enCours: boolean; raisonSansLecture: string | null;
}) {
  const conteneur = useRef<HTMLDivElement>(null);
  useEffect(() => {
    // Amène l'extrait demandé en vue dans le panneau de texte (sans faire défiler toute la page sur mobile).
    const c = conteneur.current;
    const el = extraitId ? c?.querySelector<HTMLElement>(`[data-extrait="${CSS.escape(extraitId)}"]`) : null;
    if (c && el && c.scrollHeight > c.clientHeight) c.scrollTop = el.offsetTop - c.offsetTop - 12;
  }, [texte.data, extraitId]);

  const infoPage = pages.find((p) => p.page_no === page);
  return (
    <Panneau serre titre={`Texte de la ${unite.toLowerCase()} ${page}`}>
      <div ref={conteneur} className="doc-texte-defilement">
        {!infoPage && (enCours
          ? <p className="petit secondaire doc-texte-vide">Lecture en cours…</p>
          : <p className="petit secondaire doc-texte-vide">Aucun texte extrait pour cette {unite.toLowerCase()}.</p>)}
        {infoPage && texte.isLoading && <Chargement lignes={4} libelle="Chargement du texte…" />}
        {infoPage && texte.isError && <EtatErreur erreur={texte.error} reessayer={texte.refetch} />}
        {texte.data && !texte.data.has_text && (
          <p className="petit secondaire doc-texte-vide">
            {enCours ? `Lecture optique de cette ${unite.toLowerCase()} en cours…`
              : `Aucun texte lisible sur cette ${unite.toLowerCase()} (page scannée) : consultez l’image du document.${raisonSansLecture ? ` ${raisonSansLecture}` : ''}`}
          </p>
        )}
        {texte.data?.ocr && <p className="doc-marque-ia"><MarqueIa /></p>}
        {texte.data && texte.data.has_text && texte.data.extraits.length === 0 && <pre className="doc-texte-brut">{texte.data.text}</pre>}
        {texte.data && texte.data.extraits.length > 0 && (
          <ol className="doc-paragraphes">
            {texte.data.extraits.map((x) => {
              const actif = x.id === extraitId;
              return (
                <li key={x.id} data-extrait={x.id} className={`doc-paragraphe ${actif ? 'doc-paragraphe-actif' : ''}`} aria-current={actif ? 'true' : undefined}>
                  <span className="doc-localisation">
                    <span className={x.locator?.startsWith('§') ? 'mono' : undefined}>{x.locator ?? `paragraphe ${x.para_no}`}</span>
                    {actif && <Badge ton="accent" icone={false}>Extrait demandé</Badge>}
                  </span>
                  {actif ? <mark className="doc-texte-extrait">{x.text}</mark> : <span className="doc-texte-extrait">{x.text}</span>}
                </li>
              );
            })}
          </ol>
        )}
      </div>
    </Panneau>
  );
}
